import { useEffect, useMemo, useRef, useState } from 'react'
import { capitalize } from 'lodash'
import { ArrowDownToLineIcon } from 'lucide-react'

import Button from '@/components/base/button'
import Typography from '@/components/base/typography'
import { useAppStore } from '@/contexts/store'
import { useToast } from '@/contexts/toast'
import { getColorCategoryLabel } from '@/utils/color'

import ButtonMask from './button-mask'
import { cn } from '@/lib/utils'
import { Color } from '@/types'

const CANVAS_WIDTH = 1080
const CANVAS_HEIGHT = 1350

type ColorDownloadProps = Color & { className?: string; debug?: boolean }

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(`Unable to load image: ${src}`))
    image.src = src
  })

const renderColorCard = async (canvas: HTMLCanvasElement, color: Color) => {
  const context = canvas.getContext('2d')

  if (!context) {
    throw new Error('Canvas 2D context is not available')
  }

  canvas.width = CANVAS_WIDTH
  canvas.height = CANVAS_HEIGHT

  const textColor = color.theme === 'dark' ? '#ffffff' : '#1d1d1b'
  const fontFamily =
    getComputedStyle(document.documentElement)
      .getPropertyValue('--font-family-helveticanow')
      .trim() || 'Helvetica, Arial, sans-serif'

  context.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)
  context.fillStyle = color.hex_color
  context.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

  const finishingUrl = color.finishing?.acf?.image_cover?.url
  if (finishingUrl) {
    const finishingImage = await loadImage(finishingUrl)
    const finishingHeight = CANVAS_HEIGHT / 2
    const scale = Math.max(
      CANVAS_WIDTH / finishingImage.naturalWidth,
      finishingHeight / finishingImage.naturalHeight
    )
    const sourceWidth = CANVAS_WIDTH / scale
    const sourceHeight = finishingHeight / scale

    context.save()
    context.globalAlpha = 0.8
    context.globalCompositeOperation = 'multiply'
    context.drawImage(
      finishingImage,
      (finishingImage.naturalWidth - sourceWidth) / 2,
      (finishingImage.naturalHeight - sourceHeight) / 2,
      sourceWidth,
      sourceHeight,
      0,
      0,
      CANVAS_WIDTH,
      finishingHeight
    )
    context.restore()
  }

  const logoSymbol = document.querySelector('#logo-full')
  const marginX = 67.5
  const marginY = 81

  if (logoSymbol) {
    const logoMarkup = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${logoSymbol.getAttribute(
      'viewBox'
    )}">${logoSymbol.innerHTML.replace(/currentColor/g, textColor)}</svg>`
    const logoImage = await loadImage(
      `data:image/svg+xml;charset=utf-8,${encodeURIComponent(logoMarkup)}`
    )
    const logoWidth = 270
    const logoHeight = 54

    context.drawImage(logoImage, marginX, marginY, logoWidth, logoHeight)
  }

  const category = getColorCategoryLabel(color)
  const titleY = CANVAS_HEIGHT - marginY
  const categoryX = CANVAS_WIDTH - marginX
  const categoryY = CANVAS_HEIGHT - marginY

  context.fillStyle = textColor
  context.textAlign = 'left'
  context.textBaseline = 'alphabetic'
  context.font = `400 64.8px ${fontFamily}`
  context.fillText(color.title, marginX, titleY, CANVAS_WIDTH - marginX * 2)

  if (category) {
    context.textAlign = 'right'
    context.font = `400 32.4px ${fontFamily}`
    context.fillText(
      `${category}${color.finishing ? `   |   ${capitalize(color.finishing?.name)}` : ''}`,
      categoryX,
      categoryY,
      CANVAS_WIDTH - marginX * 2
    )
  }
}

const canvasToBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      blob ? resolve(blob) : reject(new Error('Unable to create image blob'))
    }, 'image/png')
  })

const getFileName = (title: string) => {
  const safeTitle = title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()

  return `coloridecora-${safeTitle || 'colore'}.png`
}

const ColorDownload = ({ className, debug = false, ...colorProps }: ColorDownloadProps) => {
  const color = useMemo(
    () => ({
      id: colorProps.id,
      title: colorProps.title,
      link: colorProps.link,
      hex_color: colorProps.hex_color,
      color_code: colorProps.color_code,
      solar_reflectance_index: colorProps.solar_reflectance_index,
      high_end_color: colorProps.high_end_color,
      recommended_fund: colorProps.recommended_fund,
      theme: colorProps.theme,
      finishing: colorProps.finishing,
      products: colorProps.products,
      taxonomies: colorProps.taxonomies,
      enabled: colorProps.enabled
    }),
    [
      colorProps.id,
      colorProps.title,
      colorProps.link,
      colorProps.hex_color,
      colorProps.color_code,
      colorProps.solar_reflectance_index,
      colorProps.high_end_color,
      colorProps.recommended_fund,
      colorProps.theme,
      colorProps.finishing,
      colorProps.products,
      colorProps.taxonomies,
      colorProps.enabled
    ]
  )
  const debugCanvasRef = useRef<HTMLCanvasElement>(null)
  const [isRendering, setIsRendering] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { showToast } = useToast()
  const { strings } = useAppStore()

  useEffect(() => {
    if (!debug || !debugCanvasRef.current) {
      return
    }

    let cancelled = false
    setIsRendering(true)
    setError(null)

    renderColorCard(debugCanvasRef.current, color)
      .catch((renderError: Error) => {
        if (!cancelled) {
          setError(renderError.message)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsRendering(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [color, debug])

  const handleDownload = async () => {
    setIsRendering(true)
    setError(null)

    try {
      const canvas = document.createElement('canvas')
      await renderColorCard(canvas, color)
      const blob = await canvasToBlob(canvas)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = getFileName(color.title)
      link.click()
      URL.revokeObjectURL(url)
      showToast({ variant: 'success', message: strings?.labels?.color_download_success })
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : 'Unable to download image')
      showToast({ variant: 'error', message: strings?.labels?.color_download_error })
    } finally {
      setIsRendering(false)
    }
  }

  return (
    <>
      <Button
        variant="secondary"
        disabled={isRendering}
        onClick={handleDownload}
        endIcon={<ArrowDownToLineIcon size={18} />}
        aria-label={strings?.labels?.color_download_aria}
        className={cn('hover:before:bg-transparent dark:hover:before:bg-transparent', className)}
      >
        <ButtonMask>
          <Typography variant="button">{strings?.labels?.color_download}</Typography>
        </ButtonMask>
      </Button>

      {debug ? (
        <aside
          className={cn(
            'fixed bottom-[2rem] left-[2rem] z-grid w-[50rem]',
            'rounded-[0.5rem] bg-white p-3 text-dark shadow-xl',
            'grid'
          )}
        >
          <Typography variant="breadcrumbs" className="mb-1.5">
            {`${error ? error : isRendering ? 'Rendering...' : '<strong>Color download debug</strong>'}`}
          </Typography>

          <canvas
            ref={debugCanvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            className="block h-auto w-full"
            role="img"
            aria-label="Anteprima dell'immagine da scaricare"
          />
        </aside>
      ) : null}
    </>
  )
}

export default ColorDownload
